import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Osobní referral odkaz: /r/<KOD>
 *
 * Uloží kód (first-touch), a poté:
 *  - přihlášený uživatel → /moje-podniky (kód se rovnou přiřadí),
 *  - nepřihlášený → /auth?redirect=/moje-podniky&ref=<KOD> (přiřadí se po registraci).
 */
export const Route = createFileRoute("/r/$code")({
  ssr: false,
  component: ReferralRedirect,
  head: () => ({
    meta: [
      { title: "Pozvánka — Cerveza.International" },
      { name: "description", content: "Registrace přes osobní pozvánku do Cerveza.International." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

function ReferralRedirect() {
  const { code } = useParams({ from: "/r/$code" });
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const clean = (code ?? "").trim().toUpperCase();
    const valid = /^[A-Za-z0-9]{4,32}$/.test(clean);

    (async () => {
      if (valid) {
        const { storeReferralCode } = await import("@/lib/referral-tracker");
        storeReferralCode(clean);
      }
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;

      // Zaznamenáme návštěvu i se zdrojem (?src=chat / ?src=email), aby se
      // aktivace z chatu a e-mailů objevila v adminu Doporučení i na mapě.
      try {
        const [{ logMapaAccess }, { currentVisitSource }] = await Promise.all([
          import("@/lib/mapa-access-log.functions"),
          import("@/lib/visit-source"),
        ]);
        const src = currentVisitSource();
        void logMapaAccess({
          data: {
            result: data.session ? "allowed" : "anonymous",
            path: `/r/${clean}`,
            lang: null,
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
            email: data.session?.user?.email ?? null,
            userId: data.session?.user?.id ?? null,
            source: src === "app" ? "referral" : src,
          },
        }).catch(() => {});
      } catch {
        /* ignore */
      }

      if (data.session) {
        try {
          const { attachStoredReferral } = await import("@/lib/referral-attach");
          await attachStoredReferral();
        } catch {
          /* ignore — zkusí se znovu při dalším přihlášení */
        }
        if (!cancelled) navigate({ to: "/moje-podniky", replace: true });
        return;
      }

      const suffix = valid ? `&ref=${encodeURIComponent(clean)}` : "";
      window.location.replace(`/auth?redirect=%2Fmoje-podniky${suffix}`);
    })();

    return () => {
      cancelled = true;
    };
  }, [code, navigate]);

  return (
    <div className="flex min-h-[50vh] items-center justify-center p-8 text-sm text-muted-foreground">
      Přesměrováváme vás…
    </div>
  );
}
